import { useOrgAndSiteContext } from "@/context";
import ExternalSitesManager from "@shared/common/external-sites-manager";
import { PageUIDataItem } from "@shared/common/types/campaign";
import { Button, Popover } from "antd";
import { Home } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import LoadingButton from "../common/form/loading-button";
import V2Tooltip from "../v2/common/v2-tooltip";

const Item = ({
  label,
  onClick,
  gtmId,
  value,
}: {
  label: string;
  onClick(): void;
  gtmId: string;
  value: string;
}) => {
  return (
    <button
      className="relative flex items-center justify-start w-full gap-2 px-3 py-2 text-black rounded-sm cursor-pointer hover:bg-cb-gray-100"
      data-gtm-id={gtmId}
      onClick={onClick}
    >
      <PageIcon value={value} />
      {label}
    </button>
  );
};

type Item = { value: string; label: string };

const PageIcon = ({ value }: { value: string }) => {
  switch (value) {
    case "HOME":
      return <Home className="w-4 h-4" />;
    default:
      return null;
  }
};

export const getItemName = (item: PageUIDataItem) => {
  return item.type === "PATHNAME"
    ? "직접입력"
    : item.type === "PRESET"
    ? item.value === "HOME"
      ? "홈"
      : ExternalSitesManager.getPageRule(item.value)?.label ?? "페이지"
    : "페이지";
};

const PageSelector = ({
  onSelect,
  currentItem,
  className,
}: {
  onSelect(value: string): void;
  currentItem: PageUIDataItem;
  className?: string;
}) => {
  const { t } = useTranslation();
  const { currentSite } = useOrgAndSiteContext();

  const pagePresets = useMemo(() => {
    const siteType = currentSite?.siteType;
    const result: Item[] = [
      {
        value: "HOME",
        label: t("editor:segment.target.panel.label.home.name"),
      },
    ];

    if (siteType) {
      const pages = ExternalSitesManager.getPageRules(siteType);
      result.push(
        ...pages.map((page) => ({
          value: page.targetValue,
          label: page.label,
        }))
      );
    }

    return result;
  }, [currentSite?.siteType, t]);

  const [open, setOpen] = useState(false);

  const currentRule = useMemo(
    () => ExternalSitesManager.getPageRule(currentItem.value),
    [currentItem.value]
  );

  return (
    <Popover
      placement="bottomLeft"
      title={
        <span className="inline-block py-1 -mx-1 text-xs font-normal leading-5 text-cb-gray-600">
          경로를 선택해 주세요
        </span>
      }
      showArrow={false}
      trigger="click"
      open={open}
      onOpenChange={(open) => {
        setOpen(open);
      }}
      content={
        <div className="-my-[12px] -mx-[16px] w-64">
          <div className="flex flex-col items-start overflow-auto max-h-80">
            {pagePresets.map((item) => (
              <Item
                key={item.value}
                label={item.label}
                onClick={() => {
                  onSelect(item.value);
                  setOpen(false);
                }}
                value={item.value}
                gtmId={`setting_target_page_preset_${item.value}`}
              />
            ))}
          </div>

          <hr />
          <div className="flex items-center justify-end p-1 text-base text-cb-gray-600">
            <LoadingButton
              size="small"
              theme="ghost"
              onClick={() => {
                setOpen(false);
                onSelect("PATHNAME");
              }}
              data-gtm-id="setting_target_page_pathname_btn"
            >
              {t("editor:segment.target.panel.label.custom.name")}
            </LoadingButton>
          </div>
        </div>
      }
    >
      <V2Tooltip
        overlayClassName="max-w-none"
        title={
          currentItem.type === "PRESET"
            ? currentRule?.getExactUrl({
                siteUrl: currentSite?.url ?? "/",
                params: currentItem.params,
              }) || (currentItem.value === "HOME" ? currentSite?.url : null)
            : null
        }
      >
        <Button
          className={twMerge(
            "flex justify-between items-center px-4",
            className
          )}
          data-gtm-id="setting_target_page_btn"
        >
          <div className="flex items-center gap-2">
            <PageIcon value={currentItem.value} />
            <span>
              {currentItem.type === "PATHNAME"
                ? "직접입력"
                : currentItem.type === "PRESET"
                ? currentItem.value === "HOME"
                  ? "홈"
                  : currentRule?.label
                : "페이지"}
            </span>
          </div>
        </Button>
      </V2Tooltip>
    </Popover>
  );
};

export default PageSelector;
